import { ref as dbRef, get, update } from 'firebase/database';
import { db } from '../firebase';
import { BOOKING_RECEIPTS_NODE, OPEN_PLAY_RECEIPTS_NODE } from '../constants/bookingConst';

/**
 * Payment receipts, stored apart from the records they belong to.
 *
 * WHY: a receipt is a base64 image of roughly 330KB, and it used to live on
 * the booking/join record itself as `paymentScreenshotUrl`. Every query that
 * touched those records downloaded it — the guest availability grid, the admin
 * day grid, the reservation queue, the export — even though only two screens
 * ever display one. The grid needs five small fields per booking and was
 * paying three hundred times that.
 *
 * The record now carries a `hasPaymentScreenshot` boolean; the image lives at
 * `bookingReceipts/{bookingId}` (or `openPlayReceipts/{joinId}`) and is read
 * only when someone actually asks to see it.
 *
 * MIGRATION: `hasReceipt()` accepts either shape, so a record written before
 * this split still reports correctly and still renders — see its note. Keep
 * that fallback until the backfill has run on every client.
 */

/**
 * Whether a booking or Open Play join has a payment receipt attached.
 *
 * Reads the flag first and falls back to the legacy inline field, so this is
 * correct on both sides of the backfill. Use this everywhere rather than
 * testing either field directly: a caller that checks only the flag treats
 * every un-backfilled record as unpaid, and for a booking sitting at
 * 'awaiting_payment' past its deadline that silently releases its slots.
 *
 * @param {object|null|undefined} record - a booking or join record.
 * @returns {boolean}
 */
export const hasReceipt = (record) => Boolean(
  record && (record.hasPaymentScreenshot || record.paymentScreenshotUrl),
);

/**
 * Stores a booking's receipt and flags the booking, as one atomic write.
 *
 * A multi-path update rather than two writes: a flag set without its image, or
 * an image stored against a booking that does not admit to having one, are
 * both states the admin screens cannot show correctly.
 *
 * @param {string} bookingKey - the booking's Firebase key.
 * @param {string} dataUrl - the compressed base64 image.
 * @returns {Promise<void>}
 * @throws {Error} when the arguments are missing or the write fails.
 */
export const saveBookingReceipt = async (bookingKey, dataUrl) => {
  if (!bookingKey || !dataUrl) {
    throw new Error('Booking key and receipt image are required');
  }
  await update(dbRef(db), {
    [`${BOOKING_RECEIPTS_NODE}/${bookingKey}`]: dataUrl,
    [`bookings/${bookingKey}/hasPaymentScreenshot`]: true,
  });
};

/**
 * Reads one booking's receipt image.
 *
 * @param {string} bookingKey - the booking's Firebase key.
 * @param {object} [record] - the booking record, when the caller already has
 *   it. Lets a record written before the split serve its inline image without
 *   a round trip that would find nothing.
 * @returns {Promise<string|null>} the base64 data URL, or null if there is none.
 */
export const getBookingReceipt = async (bookingKey, record = null) => {
  if (record?.paymentScreenshotUrl) return record.paymentScreenshotUrl;
  if (!bookingKey) return null;
  const snapshot = await get(dbRef(db, `${BOOKING_RECEIPTS_NODE}/${bookingKey}`));
  return snapshot.exists() ? snapshot.val() : null;
};

/**
 * Stores an Open Play join's receipt and flags the join, as one atomic write.
 *
 * Takes the status alongside, because a join's receipt arriving is exactly
 * what moves it out of 'pending_payment' — writing the two separately would
 * leave a paid join invisible to the review queue if the second write failed.
 *
 * @param {string} joinKey - the join's Firebase key.
 * @param {string} dataUrl - the compressed base64 image.
 * @param {string} status - the status to set alongside, e.g. 'pending'.
 * @returns {Promise<void>}
 * @throws {Error} when the arguments are missing or the write fails.
 */
export const saveOpenPlayReceipt = async (joinKey, dataUrl, status) => {
  if (!joinKey || !dataUrl) {
    throw new Error('Join key and receipt image are required');
  }
  await update(dbRef(db), {
    [`${OPEN_PLAY_RECEIPTS_NODE}/${joinKey}`]: dataUrl,
    [`openPlayJoins/${joinKey}/hasPaymentScreenshot`]: true,
    [`openPlayJoins/${joinKey}/status`]: status,
  });
};

/**
 * Reads one Open Play join's receipt image.
 *
 * @param {string} joinKey - the join's Firebase key.
 * @param {object} [record] - the join record, when the caller already has it.
 * @returns {Promise<string|null>} the base64 data URL, or null if there is none.
 */
export const getOpenPlayReceipt = async (joinKey, record = null) => {
  if (record?.paymentScreenshotUrl) return record.paymentScreenshotUrl;
  if (!joinKey) return null;
  const snapshot = await get(dbRef(db, `${OPEN_PLAY_RECEIPTS_NODE}/${joinKey}`));
  return snapshot.exists() ? snapshot.val() : null;
};
